/**
 * Inventory MV2 query API — isolated from MV1 pages/Inventory.jsx.
 * Per-endpoint fetchers (MV1-style resilience). Shared endpoint shapes are
 * TI-ready for future TS /ti/ staff portion wiring via ti_inventory_helpers.py.
 */
import api from "@/lib/api";

/** @deprecated use section keys below — kept for guard migration */
export const INVENTORY_GLOBAL_QUERY_KEY = "inventory-global";
/** @deprecated use section keys below — kept for guard migration */
export const INVENTORY_SCOPED_QUERY_KEY = "inventory-scoped";

export const INVENTORY_DAILY_EXPENSE_ACCOUNTS_KEY = "inventory-daily-expense-accounts";
export const INVENTORY_INGREDIENT_PRICES_KEY = "inventory-ingredient-prices";
export const INVENTORY_CAPEX_ACCOUNTS_KEY = "inventory-capex-accounts";
export const INVENTORY_SETTINGS_KEY = "inventory-settings";
export const INVENTORY_STOCK_FORECAST_KEY = "inventory-stock-forecast";
export const INVENTORY_CAPACITY_REPORT_KEY = "inventory-capacity-report";
export const INVENTORY_GST_MONTHLY_KEY = "inventory-gst-monthly";

export const INVENTORY_CAPEX_ENTRIES_KEY = "inventory-capex-entries";
export const INVENTORY_FLAVOR_SALES_KEY = "inventory-flavor-sales";
export const INVENTORY_DECOR_SALES_KEY = "inventory-decor-sales";
export const INVENTORY_MATERIAL_COSTS_KEY = "inventory-material-costs";
export const INVENTORY_PACKAGING_COSTS_KEY = "inventory-packaging-costs";
export const INVENTORY_PURCHASES_KEY = "inventory-purchases";
export const INVENTORY_DAILY_EXPENSE_ENTRIES_KEY = "inventory-daily-expense-entries";

export const INVENTORY_GLOBAL_SECTION_KEYS = [
  INVENTORY_DAILY_EXPENSE_ACCOUNTS_KEY,
  INVENTORY_INGREDIENT_PRICES_KEY,
  INVENTORY_CAPEX_ACCOUNTS_KEY,
  INVENTORY_SETTINGS_KEY,
  INVENTORY_STOCK_FORECAST_KEY,
  INVENTORY_CAPACITY_REPORT_KEY,
  INVENTORY_GST_MONTHLY_KEY,
];

export const INVENTORY_SCOPED_SECTION_KEYS = [
  INVENTORY_CAPEX_ENTRIES_KEY,
  INVENTORY_FLAVOR_SALES_KEY,
  INVENTORY_DECOR_SALES_KEY,
  INVENTORY_MATERIAL_COSTS_KEY,
  INVENTORY_PACKAGING_COSTS_KEY,
  INVENTORY_PURCHASES_KEY,
  INVENTORY_DAILY_EXPENSE_ENTRIES_KEY,
];

export function buildDateParams(filters = {}) {
  const params = {};
  if (filters.start_date) params.start_date = filters.start_date;
  if (filters.end_date) params.end_date = filters.end_date;
  return params;
}

export function getDefaultInventoryFilters() {
  const now = new Date();
  const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const lastDayStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`;
  return { start_date: firstDay, end_date: lastDayStr };
}

function isAbortError(error, signal) {
  if (signal?.aborted) return true;
  return error?.name === "AbortError" || error?.code === "ERR_CANCELED";
}

async function safeSectionFetch(label, signal, fetcher, fallback) {
  try {
    const data = await fetcher();
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    return { data, error: null };
  } catch (error) {
    if (isAbortError(error, signal)) throw error;
    console.error(`Inventory MV2: failed to fetch ${label}:`, error);
    return { data: fallback, error };
  }
}

export async function fetchDailyExpenseAccountsFromApi(signal) {
  return safeSectionFetch(
    "daily expense accounts",
    signal,
    async () => {
      const res = await api.get("/daily-expense-accounts", { signal });
      return res.data.accounts || [];
    },
    []
  );
}

export async function fetchIngredientPricesFromApi(signal) {
  return safeSectionFetch(
    "ingredient prices",
    signal,
    async () => {
      const res = await api.get("/ingredient-prices", { signal });
      return res.data.prices || [];
    },
    []
  );
}

export async function fetchCapexAccountsFromApi(signal) {
  return safeSectionFetch(
    "capex accounts",
    signal,
    async () => {
      const res = await api.get("/capex-accounts", { signal });
      return res.data.accounts || [];
    },
    []
  );
}

export async function fetchInventorySettingsFromApi(signal) {
  return safeSectionFetch(
    "app settings",
    signal,
    async () => {
      const res = await api.get("/app-settings", { signal });
      return res.data;
    },
    null
  );
}

export async function fetchStockForecastFromApi(signal) {
  return safeSectionFetch(
    "stock forecast",
    signal,
    async () => {
      const res = await api.get("/inventory/stock-forecast", { params: { days: 120 }, signal });
      return res.data || null;
    },
    null
  );
}

export async function fetchCapacityReportFromApi(signal) {
  return safeSectionFetch(
    "capacity report",
    signal,
    async () => {
      const res = await api.get("/inventory/capacity-report", { signal });
      return res.data || null;
    },
    null
  );
}

export async function fetchGstMonthlyFromApi(signal) {
  // Backend route /gst-tax-paid-monthly is not implemented yet (MV1 GST tab is silently empty too).
  return safeSectionFetch(
    "GST monthly entries",
    signal,
    async () => {
      const res = await api.get("/gst-tax-paid-monthly", { signal });
      return res.data.entries || [];
    },
    []
  );
}

export async function fetchCapexEntriesFromApi(filters, signal) {
  const params = buildDateParams(filters);
  return safeSectionFetch(
    "capex entries",
    signal,
    async () => {
      const res = await api.get("/capex-entries", { params, signal });
      return {
        entries: res.data.entries || [],
        total: res.data.total || 0,
      };
    },
    { entries: [], total: 0 }
  );
}

export async function fetchFlavorSalesFromApi(filters, signal) {
  const params = buildDateParams(filters);
  return safeSectionFetch(
    "flavor sales",
    signal,
    async () => {
      const res = await api.get("/inventory/flavor-sales", { params, signal });
      return {
        flavorSales: res.data.flavor_sales || [],
        flavorTotals: {
          total_weight_kg: res.data.total_weight_kg || 0,
          total_revenue: res.data.total_revenue || 0,
        },
      };
    },
    {
      flavorSales: [],
      flavorTotals: { total_weight_kg: 0, total_revenue: 0 },
    }
  );
}

export async function fetchDecorSalesFromApi(filters, signal) {
  const params = buildDateParams(filters);
  return safeSectionFetch(
    "decor sales",
    signal,
    async () => {
      const res = await api.get("/inventory/decor-sales", { params, signal });
      return res.data.decor_sales || [];
    },
    []
  );
}

export async function fetchMaterialCostsFromApi(filters, signal) {
  const params = buildDateParams(filters);
  return safeSectionFetch(
    "material costs",
    signal,
    async () => {
      const res = await api.get("/inventory/material-costs", { params, signal });
      return {
        materialCosts: res.data.material_costs || [],
        materialTotals: {
          grand_total: res.data.grand_total || 0,
          orders_processed: res.data.orders_processed || 0,
          total_ingredients: res.data.total_ingredients || 0,
        },
      };
    },
    {
      materialCosts: [],
      materialTotals: { grand_total: 0, orders_processed: 0, total_ingredients: 0 },
    }
  );
}

export async function fetchPackagingCostsFromApi(filters, signal) {
  const params = buildDateParams(filters);
  return safeSectionFetch(
    "packaging costs",
    signal,
    async () => {
      const res = await api.get("/inventory/packaging-costs", { params, signal });
      return {
        packagingCosts: res.data.packaging_costs || [],
        packagingTotals: {
          grand_total: res.data.grand_total || 0,
          orders_with_packaging: res.data.orders_with_packaging || 0,
          total_orders: res.data.total_orders || 0,
        },
      };
    },
    {
      packagingCosts: [],
      packagingTotals: { grand_total: 0, orders_with_packaging: 0, total_orders: 0 },
    }
  );
}

export async function fetchPurchasesFromApi(filters, signal) {
  const params = buildDateParams(filters);
  return safeSectionFetch(
    "purchases",
    signal,
    async () => {
      const res = await api.get("/purchases", { params, signal });
      return {
        purchases: res.data.purchases || [],
        purchasesTotal: res.data.total || 0,
      };
    },
    { purchases: [], purchasesTotal: 0 }
  );
}

export async function fetchDailyExpenseEntriesFromApi(filters, signal) {
  const params = buildDateParams(filters);
  return safeSectionFetch(
    "daily expense entries",
    signal,
    async () => {
      const res = await api.get("/daily-expense-entries", { params, signal });
      return {
        entries: res.data.entries || [],
        total: res.data.total || 0,
      };
    },
    { entries: [], total: 0 }
  );
}

/** Legacy monolithic fetchers — delegate to section fetchers (no Promise.all reject). */
export async function fetchInventoryGlobalFromApi(signal) {
  const [
    dailyExpenseAccountsRes,
    ingredientPricesRes,
    capexAccountsRes,
    settingsRes,
    stockForecastRes,
    capacityReportRes,
    gstRes,
  ] = await Promise.all([
    fetchDailyExpenseAccountsFromApi(signal),
    fetchIngredientPricesFromApi(signal),
    fetchCapexAccountsFromApi(signal),
    fetchInventorySettingsFromApi(signal),
    fetchStockForecastFromApi(signal),
    fetchCapacityReportFromApi(signal),
    fetchGstMonthlyFromApi(signal),
  ]);
  const settings = settingsRes.data;
  return {
    dailyExpenseAccounts: dailyExpenseAccountsRes.data,
    ingredientPrices: ingredientPricesRes.data,
    capexAccounts: capexAccountsRes.data,
    settings,
    stockForecast: stockForecastRes.data,
    capacityReport: capacityReportRes.data,
    gstMonthlyEntries: gstRes.data,
    packagingFormSeed: settings,
    addonsFormSeed: settings
      ? {
          small_topper_cost: settings.small_topper_cost || 6.5,
          large_topper_cost: settings.large_topper_cost || 20.0,
          cutlery_kit_cost: settings.cutlery_kit_cost || 5.0,
        }
      : null,
  };
}

export async function fetchInventoryScopedFromApi(filters, signal) {
  const [
    capexEntriesRes,
    flavorSalesRes,
    decorSalesRes,
    materialCostsRes,
    packagingCostsRes,
    purchasesRes,
    dailyExpenseEntriesRes,
  ] = await Promise.all([
    fetchCapexEntriesFromApi(filters, signal),
    fetchFlavorSalesFromApi(filters, signal),
    fetchDecorSalesFromApi(filters, signal),
    fetchMaterialCostsFromApi(filters, signal),
    fetchPackagingCostsFromApi(filters, signal),
    fetchPurchasesFromApi(filters, signal),
    fetchDailyExpenseEntriesFromApi(filters, signal),
  ]);
  return {
    capexEntries: capexEntriesRes.data.entries,
    capexTotal: capexEntriesRes.data.total,
    flavorSales: flavorSalesRes.data.flavorSales,
    flavorTotals: flavorSalesRes.data.flavorTotals,
    decorSales: decorSalesRes.data,
    materialCosts: materialCostsRes.data.materialCosts,
    materialTotals: materialCostsRes.data.materialTotals,
    packagingCosts: packagingCostsRes.data.packagingCosts,
    packagingTotals: packagingCostsRes.data.packagingTotals,
    purchases: purchasesRes.data.purchases,
    purchasesTotal: purchasesRes.data.purchasesTotal,
    dailyExpenseEntries: dailyExpenseEntriesRes.data.entries,
    dailyExpenseTotal: dailyExpenseEntriesRes.data.total,
  };
}
